import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, MessageSquare, Send, UserCheck, Users, Inbox, CheckCircle2, Search,
  Sparkles, Phone, Mail, MapPin, RefreshCw, Bot, Link2, Settings2, Clock, ArrowRightLeft,
  AlertTriangle, CalendarCheck, HeartHandshake, Flame, Plus, Trash2, Video,
} from "lucide-react";

const NAVY = "#1C4358";
const FILTERS = [
  { key: "active", label: "Active", icon: MessageSquare },
  { key: "waiting", label: "Waiting for human", icon: Clock },
  { key: "mine", label: "Assigned to me", icon: UserCheck },
  { key: "unassigned", label: "Unassigned", icon: Users },
  { key: "missed", label: "Offline / Missed", icon: AlertTriangle },
  { key: "closed", label: "Closed", icon: CheckCircle2 },
];
const LANES = [
  { slug: "selling-the-home", label: "Selling the Home" },
  { slug: "senior-living-placement", label: "Senior Living Placement" },
  { slug: "keep-mama-home", label: "Keep Mama Home" },
  { slug: "move-mama-into-my-home", label: "Move Mama Into My Home" },
  { slug: "downsizing-estate-support", label: "Downsizing & Estate" },
  { slug: "family-transition-roadmap", label: "Transition Roadmap" },
  { slug: "start-here", label: "Start Here" },
];
const statusColor = (s) => ({
  ai_active: ["rgba(155,185,206,0.3)", NAVY], waiting_human: ["rgba(183,121,31,0.16)", "#B7791F"],
  human_active: ["rgba(47,125,90,0.14)", "#2F7D5A"], offline: ["rgba(156,30,34,0.12)", "#9C1E22"],
  resolved: ["rgba(75,85,99,0.12)", "#4B5563"], closed: ["rgba(75,85,99,0.12)", "#4B5563"],
}[s] || ["rgba(75,85,99,0.12)", "#4B5563"]);
const statusLabel = (s) => ({ ai_active: "AI active", waiting_human: "Waiting", human_active: "You / agent", offline: "After hours", resolved: "Resolved", closed: "Closed" }[s] || s);

export default function ChatInbox() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isAdmin = !!user?.is_admin;
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const [lane, setLane] = useState("all");
  const [urgency, setUrgency] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(params.get("session") || null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [meta, setMeta] = useState({ service_lanes: [], territories: [], franchises: [], roles: [] });
  const [staff, setStaff] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const threadRef = useRef(null);

  const loadInbox = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/inbox", {
        params: { filter, q: q || undefined, lane: lane === "all" ? undefined : lane, urgency: urgency === "all" ? undefined : urgency },
      });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch { toast.error("Could not load chats."); }
    finally { setLoading(false); }
  }, [filter, q, lane, urgency]);

  useEffect(() => { loadInbox(); }, [loadInbox]);
  useEffect(() => { const t = setInterval(loadInbox, 6000); return () => clearInterval(t); }, [loadInbox]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const { data } = await api.get(`/chat/sessions/${id}`);
      setDetail(data);
      requestAnimationFrame(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open chat."); }
  }, []);

  useEffect(() => { if (selId) loadDetail(selId); }, [selId, loadDetail]);
  useEffect(() => { if (!selId) return; const t = setInterval(() => loadDetail(selId), 5000); return () => clearInterval(t); }, [selId, loadDetail]);

  const openSession = (id) => { setSelId(id); setParams({ session: id }); };

  const send = async () => {
    if (!reply.trim() || !selId) return;
    setSending(true);
    try {
      await api.post(`/chat/sessions/${selId}/messages`, { text: reply.trim() });
      setReply(""); await loadDetail(selId); loadInbox();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
    finally { setSending(false); }
  };
  const takeover = async () => { try { await api.post(`/chat/sessions/${selId}/takeover`); toast.success("You've joined the chat."); loadDetail(selId); loadInbox(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); } };
  const resumeAi = async () => { try { await api.post(`/chat/sessions/${selId}/resume-ai`); loadDetail(selId); } catch { toast.error("Failed."); } };
  const resolve = async (createTask) => { try { await api.post(`/chat/sessions/${selId}/resolve`, { create_task: !!createTask }); toast.success("Chat resolved."); loadDetail(selId); loadInbox(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); } };
  const guestLink = async () => {
    try { const { data } = await api.post(`/chat/sessions/${selId}/guest-link`); await navigator.clipboard.writeText(data.link).catch(() => {}); toast.success("Guest continuation link copied to clipboard."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const markUrgent = async () => {
    try { const { data } = await api.post(`/chat/sessions/${selId}/urgent`); toast.success(data.flagged_urgent ? "Marked urgent." : "Urgent flag cleared."); loadDetail(selId); loadInbox(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const sendBooking = async () => {
    try { await api.post(`/chat/sessions/${selId}/send-booking`); toast.success("Consultation booking link sent."); loadDetail(selId); loadInbox(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const doReassign = async () => {
    if (!reassignTo) return;
    try { await api.post(`/chat/sessions/${selId}/reassign`, { user_id: reassignTo }); toast.success("Reassigned."); setReassignOpen(false); setReassignTo(""); loadDetail(selId); loadInbox(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const openSettings = async () => {
    try { const { data } = await api.get("/chat/settings"); setSettings(data.settings); setStaff(data.staff || []); setMeta({ service_lanes: data.service_lanes || [], territories: data.territories || [], franchises: data.franchises || [], roles: data.roles || [] }); setSettingsOpen(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot open settings."); }
  };
  const saveSettings = async () => {
    try {
      await api.put("/chat/settings", {
        chat_enabled: settings.chat_enabled, ai_enabled: settings.ai_enabled,
        human_handoff_enabled: settings.human_handoff_enabled, auto_create_lead: settings.auto_create_lead,
        save_transcript: settings.save_transcript, guest_continuation_enabled: settings.guest_continuation_enabled,
        offline_message: settings.offline_message, welcome_message: settings.welcome_message,
        business_hours: settings.business_hours, notify_recipients: settings.notify_recipients,
        escalation_keywords: settings.escalation_keywords, suggested_topics: settings.suggested_topics,
        quick_replies: settings.quick_replies, booking_url: settings.booking_url,
        routing_rules: settings.routing_rules, sms_enabled: settings.sms_enabled,
        sms_compliance_confirmed: settings.sms_compliance_confirmed,
        qa_review_enabled: settings.qa_review_enabled,
      });
      toast.success("Chat settings saved."); setSettingsOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };
  // routing rule helpers
  const addRule = () => setSettings((p) => ({ ...p, routing_rules: [...(p.routing_rules || []), {
    id: `r_${Date.now()}`, name: "New rule", active: true,
    match: { service_lanes: [], territory_ids: [], franchise_ids: [], state: "", start: "", end: "", days: [] },
    recipient_roles: [], recipients: [] }] }));
  const updateRule = (i, patch) => setSettings((p) => { const rr = [...(p.routing_rules || [])]; rr[i] = { ...rr[i], ...patch }; return { ...p, routing_rules: rr }; });
  const updateRuleMatch = (i, patch) => setSettings((p) => { const rr = [...(p.routing_rules || [])]; rr[i] = { ...rr[i], match: { ...(rr[i].match || {}), ...patch } }; return { ...p, routing_rules: rr }; });
  const removeRule = (i) => setSettings((p) => ({ ...p, routing_rules: (p.routing_rules || []).filter((_, idx) => idx !== i) }));
  const toggleInList = (arr, val) => (arr || []).includes(val) ? (arr || []).filter((x) => x !== val) : [...(arr || []), val];
  const loadStaffForReassign = async () => { if (staff.length) return; try { const { data } = await api.get("/chat/settings"); setStaff(data.staff || []); } catch { /* */ } };

  const s = detail?.session;

  return (
    <div className="max-w-6xl mx-auto pb-10" data-testid="chat-inbox">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare size={22} style={{ color: NAVY }} />
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">Live Chat Inbox</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadInbox} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><RefreshCw size={14} /> Refresh</Button>
          {isSuper && <Button variant="outline" size="sm" data-testid="chat-settings-btn" onClick={openSettings} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Settings2 size={14} /> Settings</Button>}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`chat-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[13px] font-semibold transition-colors ${filter === f.key ? "bg-[#1C4358] text-white" : "bg-white border border-[#E6EDF2] text-[#1C4358] hover:bg-[#1C4358]/6"}`}>
            <f.icon size={14} /> {f.label}
            {counts[f.key] > 0 && <span className={`ml-1 rounded-full px-1.5 text-[11px] ${filter === f.key ? "bg-white/25" : "bg-[#9C1E22] text-white"}`}>{counts[f.key]}</span>}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        {/* List */}
        <div className="lg:col-span-5">
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1"><Search size={15} className="absolute left-2.5 top-3 text-[#4B5563]" /><Input data-testid="chat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / email" className="pl-8 h-10 text-[14px] rounded-lg border-[#1C4358]/20" /></div>
            <Select value={lane} onValueChange={setLane}><SelectTrigger className="w-[130px] h-10 text-[13px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All lanes</SelectItem>{LANES.map((l) => <SelectItem key={l.slug} value={l.slug}>{l.label}</SelectItem>)}</SelectContent></Select>
          </div>
          {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" size={26} /></div> : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {items.length === 0 ? <div className="text-center py-16 text-[#4B5563]"><Inbox className="mx-auto mb-2" /> No chats here.</div> :
                items.map((it) => {
                  const [bg, c] = statusColor(it.status);
                  return (
                    <button key={it.id} data-testid={`chat-row-${it.id}`} onClick={() => openSession(it.id)}
                      className={`w-full text-left rounded-xl border p-3 transition-colors ${selId === it.id ? "border-[#1C4358] bg-[#1C4358]/5" : "border-[#E6EDF2] bg-white hover:border-[#1C4358]/30"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-[#1C4358] text-[15px] truncate flex items-center gap-1.5">
                          {it.unread_for_staff && <span className="h-2 w-2 rounded-full bg-[#9C1E22]" />}{it.visitor?.name || "Visitor"}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded shrink-0" style={{ backgroundColor: bg, color: c }}>{statusLabel(it.status)}</span>
                      </div>
                      <div className="text-[12px] text-[#4B5563] mt-0.5 truncate">{it.service_lane_label || "Not sure yet"}{it.visitor?.phone ? ` · ${it.visitor.phone}` : ""}</div>
                      {it.relationship_label && <div className="text-[11px] text-[#1C4358] mt-0.5 truncate flex items-center gap-1"><HeartHandshake size={10} /> Helping: {it.relationship_label}</div>}
                      {it.flagged_urgent && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#9C1E22] mt-0.5"><Flame size={10} /> Urgent</span>}
                      {it.ai_summary && <div className="text-[12px] text-[#252221] mt-1 line-clamp-2">{it.ai_summary}</div>}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {!s ? (
            <div className="rounded-xl border border-[#E6EDF2] bg-white h-[70vh] flex flex-col items-center justify-center text-[#4B5563]">
              <MessageSquare size={34} className="mb-2 opacity-40" /> Select a chat to view the conversation.
            </div>
          ) : (
            <div className="rounded-xl border border-[#E6EDF2] bg-white flex flex-col h-[70vh]" data-testid="chat-detail">
              {/* head */}
              <div className="px-4 py-3 border-b border-[#E6EDF2]">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-display font-semibold text-[#1C4358] flex items-center gap-2">
                      {s.visitor?.name || "Visitor"}
                      {s.flagged_urgent && <span data-testid="chat-urgent-badge" className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#9C1E22]/12 text-[#9C1E22]"><Flame size={11} /> URGENT</span>}
                    </div>
                    <div className="text-[12px] text-[#4B5563] flex items-center gap-2 flex-wrap mt-0.5">
                      {s.relationship_label && <span className="flex items-center gap-1 text-[#1C4358] font-medium" data-testid="chat-relationship-label"><HeartHandshake size={11} /> Helping: {s.relationship_label}</span>}
                      {s.visitor?.phone && <span className="flex items-center gap-1"><Phone size={11} /> {s.visitor.phone}</span>}
                      {s.visitor?.email && <span className="flex items-center gap-1"><Mail size={11} /> {s.visitor.email}</span>}
                      {(s.visitor?.city || s.visitor?.state) && <span className="flex items-center gap-1"><MapPin size={11} /> {s.visitor.city} {s.visitor.state}</span>}
                    </div>
                  </div>
                  <Badge className="bg-[#9BB9CE]/25 text-[#1C4358] border-0">{s.service_lane_label || "Not sure yet"}</Badge>
                </div>
                {s.ai_summary && (
                  <div className="mt-2 rounded-lg bg-[#FBF6EC] border border-[#1C4358]/10 p-2.5 text-[13px] text-[#252221] flex items-start gap-2" data-testid="chat-ai-summary">
                    <Sparkles size={14} className="text-[#9C1E22] shrink-0 mt-0.5" /> <span><strong className="text-[#1C4358]">AI summary (internal):</strong> {s.ai_summary}</span>
                  </div>
                )}
              </div>

              {/* thread */}
              <div ref={threadRef} className="flex-1 overflow-y-auto px-4 py-3 bg-[#FBF6EC]/40">
                {(detail.messages || []).map((m) => {
                  if (m.role === "system") return <div key={m.id} className="flex justify-center my-2"><span className="text-[11px] text-[#4B5563] bg-[#1C4358]/6 rounded-full px-2.5 py-1">{m.text}</span></div>;
                  const right = m.role === "agent";
                  const isAi = m.role === "ai";
                  return (
                    <div key={m.id} className={`flex ${right ? "justify-end" : "justify-start"} mb-2`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-[14px] ${right ? "bg-[#2F7D5A] text-white" : isAi ? "bg-white border border-[#1C4358]/12 text-[#252221]" : "bg-[#1C4358] text-white"}`}>
                        <div className="text-[10px] opacity-70 mb-0.5 flex items-center gap-1">{isAi && <Bot size={10} />}{m.sender_name || (m.role === "visitor" ? "Visitor" : m.role)}</div>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* actions */}
              <div className="border-t border-[#E6EDF2] p-3">
                <div className="flex flex-wrap gap-2 mb-2">
                  {s.status !== "human_active" && <Button size="sm" data-testid="chat-takeover-btn" onClick={takeover} className="gap-1 bg-[#1C4358] hover:bg-[#163645] text-white"><UserCheck size={14} /> Take over</Button>}
                  {s.ai_paused && <Button size="sm" variant="outline" onClick={resumeAi} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Bot size={14} /> Resume AI</Button>}
                  {isAdmin && <Button size="sm" variant="outline" data-testid="chat-reassign-btn" onClick={() => { loadStaffForReassign(); setReassignOpen(true); }} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><ArrowRightLeft size={14} /> Reassign</Button>}
                  <Button size="sm" variant="outline" data-testid="chat-urgent-btn" onClick={markUrgent} className={`gap-1 ${s.flagged_urgent ? "border-[#9C1E22] text-[#9C1E22] bg-[#9C1E22]/6" : "border-[#D6E0E7] text-[#1C4358]"}`}><Flame size={14} /> {s.flagged_urgent ? "Urgent" : "Mark urgent"}</Button>
                  <Button size="sm" variant="outline" data-testid="chat-booking-btn" onClick={sendBooking} className="gap-1 border-[#2C6480]/40 text-[#2C6480]"><CalendarCheck size={14} /> Send booking link</Button>
                  <Button size="sm" variant="outline" data-testid="chat-guest-link-btn" onClick={guestLink} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Link2 size={14} /> Guest link</Button>
                  <Button size="sm" variant="outline" data-testid="chat-meeting-btn" onClick={() => navigate(`/meetings?new=1&chat=${selId}${s.lead_id ? `&lead=${s.lead_id}` : ""}`)} className="gap-1 border-[#1C4358]/40 text-[#1C4358]"><Video size={14} /> Create meeting</Button>
                  {s.status !== "resolved" && s.status !== "closed" && <Button size="sm" variant="outline" data-testid="chat-resolve-btn" onClick={() => resolve(true)} className="gap-1 border-[#2F7D5A]/40 text-[#2F7D5A]"><CheckCircle2 size={14} /> Resolve + task</Button>}
                </div>
                {(detail.quick_replies || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="chat-quick-replies">
                    {(detail.quick_replies || []).map((qr, i) => (
                      <button key={i} data-testid={`chat-quick-reply-${i}`} onClick={() => setReply(qr)}
                        className="text-[12px] rounded-full border border-[#1C4358]/20 bg-white text-[#1C4358] px-2.5 py-1 hover:bg-[#1C4358]/6 transition-colors max-w-[220px] truncate">{qr}</button>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <Textarea data-testid="chat-agent-input" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Type a reply… (AI pauses when you send)" className="flex-1 resize-none text-[15px] rounded-lg border-[#1C4358]/20" />
                  <Button data-testid="chat-agent-send" onClick={send} disabled={sending || !reply.trim()} className="h-11 w-11 p-0 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reassign dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent data-testid="chat-reassign-dialog">
          <DialogHeader><DialogTitle>Reassign chat</DialogTitle></DialogHeader>
          <Select value={reassignTo} onValueChange={setReassignTo}>
            <SelectTrigger data-testid="chat-reassign-select"><SelectValue placeholder="Choose a team member" /></SelectTrigger>
            <SelectContent>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button><Button data-testid="chat-reassign-confirm" onClick={doReassign} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white">Reassign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="chat-settings-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Chat settings</DialogTitle></DialogHeader>
          {settings && (
            <div className="space-y-4">
              {[
                ["chat_enabled", "Chat is ON"], ["ai_enabled", "AI assistant ON"],
                ["human_handoff_enabled", "Human handoff ON"], ["auto_create_lead", "Auto-create CRM lead"],
                ["save_transcript", "Save transcripts"], ["guest_continuation_enabled", "Allow guest continuation"],
              ].map(([k, lbl]) => (
                <label key={k} className="flex items-center justify-between gap-3 text-[14px]">
                  <span className="text-[#252221]">{lbl}</span>
                  <Switch checked={!!settings[k]} data-testid={`chat-set-${k}`} onCheckedChange={(v) => setSettings((p) => ({ ...p, [k]: v }))} />
                </label>
              ))}
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Welcome message</Label><Textarea rows={2} value={settings.welcome_message || ""} onChange={(e) => setSettings((p) => ({ ...p, welcome_message: e.target.value }))} className="text-[14px] rounded-lg border-[#1C4358]/20" /></div>
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Offline / after-hours message</Label><Textarea rows={2} value={settings.offline_message || ""} onChange={(e) => setSettings((p) => ({ ...p, offline_message: e.target.value }))} className="text-[14px] rounded-lg border-[#1C4358]/20" /></div>
              <label className="flex items-center justify-between gap-3 text-[14px]">
                <span className="text-[#252221]">Enforce business hours (else always online)</span>
                <Switch checked={!!settings.business_hours?.enabled} data-testid="chat-set-hours" onCheckedChange={(v) => setSettings((p) => ({ ...p, business_hours: { ...p.business_hours, enabled: v } }))} />
              </label>
              {settings.business_hours?.enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Start</Label><Input value={settings.business_hours.start} onChange={(e) => setSettings((p) => ({ ...p, business_hours: { ...p.business_hours, start: e.target.value } }))} placeholder="08:00" className="h-9" /></div>
                  <div><Label className="text-[12px]">End</Label><Input value={settings.business_hours.end} onChange={(e) => setSettings((p) => ({ ...p, business_hours: { ...p.business_hours, end: e.target.value } }))} placeholder="18:00" className="h-9" /></div>
                </div>
              )}
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Escalation keywords (comma separated)</Label>
                <Textarea rows={2} value={(settings.escalation_keywords || []).join(", ")} onChange={(e) => setSettings((p) => ({ ...p, escalation_keywords: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))} className="text-[13px] rounded-lg border-[#1C4358]/20" />
              </div>
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Consultation booking link (path or URL)</Label>
                <Input data-testid="chat-set-booking-url" value={settings.booking_url || ""} onChange={(e) => setSettings((p) => ({ ...p, booking_url: e.target.value }))} placeholder="/free-consultation" className="h-9 text-[14px] rounded-lg border-[#1C4358]/20" />
              </div>
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Quick replies (one per line)</Label>
                <Textarea data-testid="chat-set-quick-replies" rows={4} value={(settings.quick_replies || []).join("\n")} onChange={(e) => setSettings((p) => ({ ...p, quick_replies: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) }))} className="text-[13px] rounded-lg border-[#1C4358]/20" />
              </div>

              {/* PROMPT 9B — Notifications & Routing */}
              <div className="pt-2 border-t border-[#E6EDF2]">
                <div className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358] mb-2">Notifications & Routing</div>
                <div className="rounded-lg bg-[#F5F8FA] border border-[#E6EDF2] p-3 space-y-2.5">
                  <p className="text-[12px] text-[#4B5563]">In-app + email notifications are always on. Rules are evaluated top-down — the first matching, active rule decides who is notified. If none match, we route by branch.</p>
                  <label className="flex items-center justify-between gap-3 text-[14px]">
                    <span className="text-[#252221]">SMS compliance / opt-in confirmed</span>
                    <Switch checked={!!settings.sms_compliance_confirmed} data-testid="chat-set-sms-compliance" onCheckedChange={(v) => setSettings((p) => ({ ...p, sms_compliance_confirmed: v, sms_enabled: v ? p.sms_enabled : false }))} />
                  </label>
                  <label className="flex items-center justify-between gap-3 text-[14px]">
                    <span className={`${settings.sms_compliance_confirmed ? "text-[#252221]" : "text-[#9AA5B1]"}`}>SMS notifications</span>
                    <Switch checked={!!settings.sms_enabled} disabled={!settings.sms_compliance_confirmed} data-testid="chat-set-sms-enabled" onCheckedChange={(v) => setSettings((p) => ({ ...p, sms_enabled: v }))} />
                  </label>
                  {!settings.sms_compliance_confirmed && (
                    <p className="text-[12px] text-[#B7791F]" data-testid="chat-sms-disabled-note">SMS notifications are disabled until a compliant provider and opt-in process are configured.</p>
                  )}
                  <label className="flex items-center justify-between gap-3 text-[14px] pt-1 border-t border-[#E6EDF2]">
                    <span className="text-[#252221]">Q&amp;A review queue (capture chat questions for admin review)</span>
                    <Switch checked={settings.qa_review_enabled !== false} data-testid="chat-set-qa-enabled" onCheckedChange={(v) => setSettings((p) => ({ ...p, qa_review_enabled: v }))} />
                  </label>
                </div>

                <div className="flex items-center justify-between mt-3 mb-1">
                  <Label className="text-[13px] font-semibold text-[#1C4358]">Routing rules</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="chat-add-rule" onClick={addRule} className="h-8 gap-1 border-[#D6E0E7] text-[#1C4358] text-[12px]"><Plus size={13} /> Add rule</Button>
                </div>
                <div className="space-y-3" data-testid="chat-routing-rules">
                  {(settings.routing_rules || []).length === 0 && <p className="text-[12px] text-[#9AA5B1]">No custom rules — routing falls back to branch/default recipients.</p>}
                  {(settings.routing_rules || []).map((r, i) => (
                    <div key={r.id || i} data-testid={`chat-rule-${i}`} className="rounded-lg border border-[#E6EDF2] p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Input value={r.name || ""} onChange={(e) => updateRule(i, { name: e.target.value })} placeholder="Rule name" className="h-8 text-[13px] flex-1" data-testid={`chat-rule-name-${i}`} />
                        <label className="flex items-center gap-1 text-[12px] text-[#4B5563]"><Switch checked={r.active !== false} onCheckedChange={(v) => updateRule(i, { active: v })} /> On</label>
                        <button type="button" onClick={() => removeRule(i)} data-testid={`chat-rule-remove-${i}`} className="text-[#9C1E22] hover:bg-[#9C1E22]/10 rounded p-1"><Trash2 size={14} /></button>
                      </div>
                      <div>
                        <div className="text-[11px] text-[#4B5563] mb-1">Match service lanes (any if none)</div>
                        <div className="flex flex-wrap gap-1.5">
                          {meta.service_lanes.map((l) => (
                            <button type="button" key={l.value} onClick={() => updateRuleMatch(i, { service_lanes: toggleInList(r.match?.service_lanes, l.value) })}
                              className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors ${(r.match?.service_lanes || []).includes(l.value) ? "border-[#1C4358] bg-[#1C4358]/8 text-[#1C4358] font-semibold" : "border-[#D6E0E7] text-[#4B5563]"}`}>{l.label}</button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={(r.match?.territory_ids || [])[0] || "any"} onValueChange={(v) => updateRuleMatch(i, { territory_ids: v === "any" ? [] : [v] })}>
                          <SelectTrigger className="h-8 text-[12px]" data-testid={`chat-rule-territory-${i}`}><SelectValue placeholder="Any territory" /></SelectTrigger>
                          <SelectContent><SelectItem value="any">Any territory</SelectItem>{meta.territories.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={r.match?.state || ""} onChange={(e) => updateRuleMatch(i, { state: e.target.value })} placeholder="State (any)" className="h-8 text-[12px]" />
                        <Input value={r.match?.start || ""} onChange={(e) => updateRuleMatch(i, { start: e.target.value })} placeholder="From HH:MM (any)" className="h-8 text-[12px]" />
                        <Input value={r.match?.end || ""} onChange={(e) => updateRuleMatch(i, { end: e.target.value })} placeholder="To HH:MM (any)" className="h-8 text-[12px]" />
                      </div>
                      <label className="flex items-center gap-1.5 text-[12px] text-[#4B5563]">
                        <input type="checkbox" checked={(r.match?.days || []).length === 5} onChange={(e) => updateRuleMatch(i, { days: e.target.checked ? [0, 1, 2, 3, 4] : [] })} /> Weekdays only (else any day)
                      </label>
                      <div>
                        <div className="text-[11px] text-[#4B5563] mb-1">Notify roles</div>
                        <div className="flex flex-wrap gap-1.5">
                          {meta.roles.map((role) => (
                            <button type="button" key={role} onClick={() => updateRule(i, { recipient_roles: toggleInList(r.recipient_roles, role) })}
                              className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors ${(r.recipient_roles || []).includes(role) ? "border-[#2F7D5A] bg-[#2F7D5A]/8 text-[#2F7D5A] font-semibold" : "border-[#D6E0E7] text-[#4B5563]"}`}>{role.replace(/_/g, " ")}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-[#4B5563] mb-1">Notify specific people</div>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {staff.map((u) => (
                            <button type="button" key={u.id} onClick={() => updateRule(i, { recipients: toggleInList(r.recipients, u.id) })}
                              className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors ${(r.recipients || []).includes(u.id) ? "border-[#9C1E22] bg-[#9C1E22]/8 text-[#9C1E22] font-semibold" : "border-[#D6E0E7] text-[#4B5563]"}`}>{u.name}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button><Button data-testid="chat-settings-save" onClick={saveSettings} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white">Save settings</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
